import React, { useState } from 'react';
import { PieWheel, DEFAULT_SLICE_COLORS, DEFAULT_SLICE_TEXT_COLORS, WheelSlice } from '../PieWheel';
import { PrimaryButton, SecondaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';

interface BillSplitGameProps {
  appName: string;
  tableSessionId: number;
  billAmount?: number;
  onClose?: () => void;
}

type Stage = 'entry' | 'spinning' | 'result';

const BillSplitGame: React.FC<BillSplitGameProps> = ({
  appName,
  tableSessionId,
  billAmount = 0,
  onClose,
}) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'BILL_SPLIT');
  const [names, setNames] = useState<string[]>(['You', 'Friend']);
  const [draft, setDraft] = useState('');
  const [stage, setStage] = useState<Stage>('entry');
  const [chosenName, setChosenName] = useState<string | null>(null);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const [spinToken, setSpinToken] = useState(0);
  const [rerollsRemaining, setRerollsRemaining] = useState(1);

  function addName() {
    const trimmed = draft.trim();
    if (!trimmed || names.includes(trimmed)) return;
    setNames((n) => [...n, trimmed]);
    setDraft('');
  }

  function removeName(name: string) {
    if (names.length <= 2) return;
    setNames((n) => n.filter((x) => x !== name));
  }

  async function handleSpin() {
    if (names.length < 2 || !eligible) return;
    setStage('spinning');
    try {
      const res = await gamesService.billSplitSpin(appName, tableSessionId, names);
      const idx = names.findIndex((n) => n === res.chosenName);
      setChosenName(res.chosenName);
      setTargetIndex(idx >= 0 ? idx : 0);
      setRerollsRemaining(res.rerollsRemaining);
      setSpinToken((t) => t + 1);
    } catch {
      setStage('entry');
      setRerollsRemaining(0);
    }
  }

  const locked = !loading && !eligible;

  const slices: WheelSlice[] = names.map((n, i) => ({
    label: n,
    color: DEFAULT_SLICE_COLORS[i % DEFAULT_SLICE_COLORS.length],
    textColor: DEFAULT_SLICE_TEXT_COLORS[i % DEFAULT_SLICE_TEXT_COLORS.length],
  }));

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={
          stage === 'entry'
            ? <>Who's <span style={{ color: '#D9472B' }}>Paying?</span></>
            : <>The Wheel Has <span style={{ color: '#D9472B' }}>Spoken!</span></>
        }
        subtitle={
          stage === 'entry'
            ? 'Add everyone at the table, then spin'
            : 'No cheating — the spin decides fairly'
        }
        onClose={onClose}
      />

      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && stage === 'entry' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {names.map((name) => (
                <div
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#FFFFFF',
                    border: '1px solid var(--border-color, #E5E2D8)',
                    borderRadius: 12,
                    padding: '10px 14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'var(--primary, #F5C21A)',
                        color: 'var(--text-on-primary, #1F2428)',
                        fontWeight: 700,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {name[0].toUpperCase()}
                    </div>
                    <span style={{ fontSize: 14 }}>{name}</span>
                  </div>
                  <button
                    onClick={() => removeName(name)}
                    disabled={names.length <= 2}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: 'var(--text-muted, #9B8F6E)',
                      cursor: names.length <= 2 ? 'not-allowed' : 'pointer',
                      opacity: names.length <= 2 ? 0.4 : 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addName()}
                placeholder="Add another name"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--border-color, #E5E2D8)',
                  fontSize: 13,
                  fontFamily: 'Manrope, sans-serif',
                  outline: 'none',
                }}
              />
              <button
                onClick={addName}
                style={{
                  padding: '0 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'var(--text-primary, #1F2428)',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 18,
                }}
              >
                +
              </button>
            </div>

            <div style={{ height: 22 }} />
            <PrimaryButton variant="ember" onClick={handleSpin} disabled={names.length < 2}>
              SPIN TO DECIDE
            </PrimaryButton>
            <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--text-muted, #9B8F6E)', marginTop: 10 }}>
              {names.length} people
              {billAmount > 0 ? ` · Bill ₹${billAmount.toLocaleString('en-IN')}` : ''}
            </p>
          </>
        )}

        {!loading && eligible && (stage === 'spinning' || stage === 'result') && (
          <>
            <div style={{ margin: '10px 0 18px' }}>
              <PieWheel
                slices={slices}
                targetIndex={targetIndex}
                spinToken={spinToken}
                onSpinComplete={() => setStage('result')}
              />
            </div>

            {stage === 'result' && chosenName && (
              <>
                <Card style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted, #9B8F6E)', letterSpacing: 1 }}>PAYING TODAY</div>
                  <div style={{ fontWeight: 800, fontSize: 27, color: '#D9472B', marginTop: 2 }}>
                    {chosenName} 🎉
                  </div>
                </Card>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  {rerollsRemaining > 0 && (
                    <div style={{ flex: 1 }}>
                      <SecondaryButton onClick={handleSpin}>Re-spin ({rerollsRemaining} left)</SecondaryButton>
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <PrimaryButton>Share 📸</PrimaryButton>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillSplitGame;
